import { Brush, X } from 'lucide-react';
import useExportStore from '@export/hooks/useExportStore';
import { Button, IconButton } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

/**
 * Trim-curve thickening options (left-middle). Shown only while the brush is active. The user
 * brushes parts of the confirmed trim curve and assigns extra wall thickness there. Applying
 * the thickness to the generated wall is a later phase — this panel only drives selection +
 * the per-point value.
 */
export function TrimThickeningPanel() {
  const {
    trimThickeningActive,
    setTrimThickeningActive,
    trimBrushRadius,
    setTrimBrushRadius,
    trimSelectedPoints,
    setTrimSelectedPoints,
    trimThicknessValue,
    applyTrimThickness,
  } = useExportStore();

  if (!trimThickeningActive) return null;

  const selectedCount = trimSelectedPoints.length;

  return (
    <div className="pointer-events-auto absolute left-4 top-1/2 z-20 flex w-[260px] -translate-y-1/2 flex-col gap-4 rounded-3xl border border-white/10 bg-black/30 p-4 text-white backdrop-blur">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Brush className="h-4 w-4 text-primary-300" />
          Thickening
        </span>
        <IconButton
          type="button"
          size="icon"
          variant="ghost"
          icon={X}
          aria-label="Close thickening"
          onClick={() => setTrimThickeningActive(false)}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Label>Brush radius</Label>
          <span className="text-xs text-white/70">{trimBrushRadius.toFixed(2)} mm</span>
        </div>
        <Slider
          min={0.25}
          max={8}
          step={0.25}
          value={[trimBrushRadius]}
          onValueChange={([value]) => setTrimBrushRadius(value)}
        />
      </div>

      <p className="text-xs text-white/60">
        Hover to highlight points; hold and drag to select. {selectedCount} point
        {selectedCount === 1 ? '' : 's'} selected.
      </p>

      {selectedCount > 0 && (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label>Extra thickness</Label>
              <span className="text-xs text-white/70">{trimThicknessValue.toFixed(2)} mm</span>
            </div>
            <Slider
              min={0}
              max={3}
              step={0.1}
              value={[trimThicknessValue]}
              onValueChange={([value]) => applyTrimThickness(value)}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => setTrimSelectedPoints([])}
          >
            Clear selection
          </Button>
        </>
      )}
    </div>
  );
}
